import {
	DimensionLimitations,
	groupWidthToTileGroupColumnSpan,
	SpaceOptions,
	TileGridConfiguration,
	TileGridRenderSettings,
	TileGridRenderState,
	TileGroupColumnSpanInTileGroupGrid,
	TileGroupInitializationModel,
	TileGroupRenderState,
	TileGroupVisibilityPreference,
	TileRenderState
} from "../common";
import { TileGroupAccessKind, TileGroupWidth } from "client/Api/Workspace/workspace";
import { InteractionState } from "../../Tiles/TileContext";
import { Guid } from "client_legacy/ndaw/guid";
import { DatabaseId, DatabaseOrTempObjectId } from "client_legacy/ndaw/tempOrDatabaseId";
import { ensureNotNil } from "client/Misc/utils";
import {
	TileGridReducerContext,
	TileGroupReducerContext,
	TileGroupChangeEffect
} from "./effects";
import { setPropertiesPreservingRefWhenUnchanged } from "./common";
import { TileDropTarget } from "../selectors/space";
import * as tileGridReducers from "./tileGrid";
import * as tileGroupSelectors from "../selectors/tileGroup";
import { GridDimension } from "client/Api/Workspace/common";

export function generateInitialState(
	context: TileGroupReducerContext,
	tileGroup: TileGroupInitializationModel,
	tileGroupOrder: number,
	automaticallyReorderTilesForNoneEditableTileGroups: boolean,
	regularGroupWidthTileGridConfiguration: TileGridConfiguration,
	tileGroupGridDimensionLimits: DimensionLimitations,
	isInEditMode: boolean
): TileGroupRenderState {
	const tileGroupColumnSpan: TileGroupColumnSpanInTileGroupGrid = getColumnSpanForGroup(
		tileGroup.data
			.width,
		tileGroupGridDimensionLimits
	);

	const groupWithoutGrid: Omit<TileGroupRenderState, "tileGrid"> = {
		data: tileGroup.data,
		visibilityPreference: { isSet: false },
		order: tileGroupOrder,
		isVisible: false, // All tile groups start of invisible until a space configuration is applied
		dimension: {
			columnSpan: tileGroupColumnSpan.value,
			rowSpan: 0 // Dummy value, will be filled in by applyTileGridRenderStateToTileGroupRenderState
		},
		// They all start invisible and on top of each other
		position: { column: 0, row: 0 }
	};

	const automaticallyReorderTiles: boolean = automaticallyReorderTilesForNoneEditableTileGroups
		&& tileGroup.data
			.accessKind !== TileGroupAccessKind.Edit;

	const tileGridRenderSettings: TileGridRenderSettings = tileGroupSelectors.createTileGridRenderSettings(
		automaticallyReorderTiles,
		regularGroupWidthTileGridConfiguration,
		tileGroupColumnSpan
	);

	const gridContext: TileGridReducerContext = context.createGridContext();

	const tileInteractionState: InteractionState = tileGroupSelectors.getTileInteractionState(
		groupWithoutGrid,
		isInEditMode
	);

	const tileGrid: TileGridRenderState = tileGridReducers.generateInitialState(
		gridContext,
		tileGroup.tiles,
		tileInteractionState,
		tileGridRenderSettings
	);

	const resultState: TileGroupRenderState = applyTileGrid(
		groupWithoutGrid,
		tileGrid
	);

	context.addEffect(TileGroupChangeEffect.ConsumedSpaceIsChanged);

	return resultState;
}

export function applyDimensionLimits(
	currentState: TileGroupRenderState,
	context: TileGroupReducerContext,
	tileGroupGridDimensionLimits: DimensionLimitations,
	regularGroupWidthTileGridConfiguration: TileGridConfiguration,
	automaticallyReorderTilesForNoneEditableTileGroups: boolean
): TileGroupRenderState {
	const tileGroupColumnSpan: TileGroupColumnSpanInTileGroupGrid = getColumnSpanForGroup(
		currentState.data
			.width,
		tileGroupGridDimensionLimits
	);

	currentState = setPropertiesPreservingRefWhenUnchanged(
		currentState,
		{
			dimension: setPropertiesPreservingRefWhenUnchanged(
				currentState.dimension,
				{
					columnSpan: tileGroupColumnSpan.value
				}
			)
		}
	);

	const automaticallyReorderTiles: boolean = automaticallyReorderTilesForNoneEditableTileGroups
		&& currentState.data
			.accessKind !== TileGroupAccessKind.Edit;

	const tileGridRenderSettings: TileGridRenderSettings = tileGroupSelectors.createTileGridRenderSettings(
		automaticallyReorderTiles,
		regularGroupWidthTileGridConfiguration,
		tileGroupColumnSpan
	);

	const gridContext: TileGridReducerContext = context.createGridContext();
	const tileGrid: TileGridRenderState = tileGridReducers.applyRenderSettings(
		currentState.tileGrid,
		gridContext,
		tileGridRenderSettings
	);

	currentState = applyTileGrid(
		currentState,
		tileGrid
	);

	return currentState;
}

export function applyEditMode(
	currentState: TileGroupRenderState,
	context: TileGroupReducerContext,
	isInEditMode: boolean,
	options: OptionalModel<SpaceOptions>
): TileGroupRenderState {
	const tileInteractionState: InteractionState = tileGroupSelectors.getTileInteractionState(
		currentState,
		isInEditMode
	);

	const isHidden: boolean = tileGroupSelectors.isHidden(
		currentState,
		options
	);

	const gridContext: TileGridReducerContext = context.createGridContext();

	const tileGrid: TileGridRenderState = tileGridReducers.applyTileInteractionState(
		currentState.tileGrid,
		gridContext,
		tileInteractionState
	);

	currentState = applyTileGrid(
		currentState,
		tileGrid
	);

	const isHiddenFromListing: boolean = tileGroupSelectors.isHiddenFromListing(
		currentState,
		isInEditMode,
		options
	);

	const wasVisible: boolean = currentState.isVisible;

	currentState = setPropertiesPreservingRefWhenUnchanged(
		currentState,
		{
			isVisible: isVisible({ isHiddenFromListing, isHidden })
		}
	);

	if (wasVisible !== currentState.isVisible) {
		context.addEffect(TileGroupChangeEffect.ConsumedSpaceIsChanged);
	}

	return currentState;
}

export function applySpacePreference(
	currentState: TileGroupRenderState,
	groupContext: TileGroupReducerContext,
	visibilityPreference: TileGroupVisibilityPreference,
	renderingOrder: number,
	spaceOptions: OptionalModel<SpaceOptions>,
	isInEditMode: boolean
): TileGroupRenderState {
	currentState = applyVisibilityPreference(
		currentState,
		groupContext,
		visibilityPreference,
		isInEditMode,
		spaceOptions
	);

	currentState = applyOrder(
		currentState,
		renderingOrder
	);

	return currentState;
}

export function applyOrder(
	currentState: TileGroupRenderState,
	order: number
): TileGroupRenderState {
	return setPropertiesPreservingRefWhenUnchanged(
		currentState,
		{ order }
	);
}

export function applyIdOfNewlyCreatedTileGroup(
	currentState: TileGroupRenderState,
	addedItemsIdsMap: ReadonlyMap<Guid, number>
): TileGroupRenderState {
	const currentId: DatabaseOrTempObjectId = currentState.data
		.id;

	if (currentId.hasDatabaseId) {
		return currentState;
	}

	if (!addedItemsIdsMap.has(currentId.tempId)) {
		console.error("Response does not contain id mapping for one of the new tile groups");

		return currentState;
	}

	return {
		...currentState,
		data: {
			...currentState.data,
			id: new DatabaseId(
				ensureNotNil(
					addedItemsIdsMap.get(currentId.tempId)
				)
			)
		}
	};
}

export function applyVisibilityPreference(
	currentState: TileGroupRenderState,
	context: TileGroupReducerContext,
	visibilityPreference: TileGroupVisibilityPreference,
	isInEditMode: boolean,
	spaceOptions: OptionalModel<SpaceOptions>
): TileGroupRenderState {
	currentState = setPropertiesPreservingRefWhenUnchanged(
		currentState,
		{ visibilityPreference }
	);

	const isHidden: boolean = tileGroupSelectors.isHidden(
		currentState,
		spaceOptions
	);

	const isHiddenFromListing: boolean = tileGroupSelectors.isHiddenFromListing(
		currentState,
		isInEditMode,
		spaceOptions
	);

	const wasVisible: boolean = currentState.isVisible;

	currentState = setPropertiesPreservingRefWhenUnchanged(
		currentState,
		{
			isVisible: isVisible({ isHiddenFromListing, isHidden })
		}
	);

	if (wasVisible !== currentState.isVisible) {
		context.addEffect(TileGroupChangeEffect.ConsumedSpaceIsChanged);
	}

	return currentState;
}

export function changeName(
	currentState: TileGroupRenderState,
	newName: string
): TileGroupRenderState {
	if (currentState.data.name === newName) {
		return currentState;
	}

	return {
		...currentState,
		data: {
			...currentState.data,
			name: newName
		}
	};
}

export function changeWidth(
	currentState: TileGroupRenderState,
	context: TileGroupReducerContext,
	newWidth: TileGroupWidth,
	tileGroupGridDimensionLimits: DimensionLimitations,
	regularGroupWidthTileGridConfiguration: TileGridConfiguration,
	automaticallyReorderTilesForNoneEditableTileGroups: boolean
): TileGroupRenderState {
	const tileGroupColumnSpan: TileGroupColumnSpanInTileGroupGrid = getColumnSpanForGroup(
		newWidth,
		tileGroupGridDimensionLimits
	);

	if (tileGroupColumnSpan.kind !== "normal") {
		throw new Error("Cannot change width of the tile group because its size is reduced.");
	}

	if (currentState.dimension.columnSpan !== tileGroupColumnSpan.value) {
		context.addEffect(TileGroupChangeEffect.ConsumedSpaceIsChanged);
	}

	currentState = setPropertiesPreservingRefWhenUnchanged(
		currentState,
		{
			data: setPropertiesPreservingRefWhenUnchanged(
				currentState.data,
				{ width: newWidth }
			),
			dimension: setPropertiesPreservingRefWhenUnchanged(
				currentState.dimension,
				{ columnSpan: tileGroupColumnSpan.value }
			)
		}
	);

	const automaticallyReorderTiles: boolean = automaticallyReorderTilesForNoneEditableTileGroups
		&& currentState.data
			.accessKind !== TileGroupAccessKind.Edit;

	const tileGridRenderSettings: TileGridRenderSettings = tileGroupSelectors.createTileGridRenderSettings(
		automaticallyReorderTiles,
		regularGroupWidthTileGridConfiguration,
		tileGroupColumnSpan
	);

	const newGridColSpan: number = tileGridRenderSettings
		.configuration
		.dimensionLimits
		.columns;

	const oldGridColSpan: number = currentState.tileGrid
		.settings
		.configuration
		.dimensionLimits
		.columns;

	const gridContext: TileGridReducerContext = context.createGridContext();
	if (newGridColSpan < oldGridColSpan) {
		const tileGrid: TileGridRenderState = tileGridReducers.adjustTilesForNewConfiguration(
			currentState.tileGrid,
			gridContext,
			tileGridRenderSettings.configuration
		);

		currentState = applyTileGrid(
			currentState,
			tileGrid
		);
	}

	const tileGrid: TileGridRenderState = tileGridReducers.applyRenderSettings(
		currentState.tileGrid,
		gridContext,
		tileGridRenderSettings
	);

	currentState = applyTileGrid(
		currentState,
		tileGrid
	);

	return currentState;
}

export function applyTileDragging(
	currentState: TileGroupRenderState,
	context: TileGroupReducerContext,
	tileId: number,
	target: TileDropTarget
): TileGroupRenderState {
	const {
		tileGroupId: targetId,
		placementArea
	} = target;

	const isTarget: boolean = currentState.data
		.id
		.equals(targetId);

	let gridRenderState: TileGridRenderState = currentState.tileGrid;
	const gridContext: TileGridReducerContext = context.createGridContext();

	if (isTarget) {
		gridRenderState = tileGridReducers.setPlaceholder(
			gridRenderState,
			gridContext,
			tileId,
			placementArea
		);
	} else {
		gridRenderState = tileGridReducers.clearPlaceholder(
			gridRenderState,
			gridContext
		);
	}

	return applyTileGrid(currentState, gridRenderState);
}

export function commitTileDragging(
	currentState: TileGroupRenderState,
	context: TileGroupReducerContext,
	tileBeingDragged: TileRenderState,
	target: TileDropTarget,
	sourceId: DatabaseOrTempObjectId
): TileGroupRenderState {
	const {
		placementArea,
		tileGroupId: targetId
	} = target;

	const isTarget: boolean = currentState.data
		.id
		.equals(targetId);

	const isSource: boolean = currentState.data
		.id
		.equals(sourceId);

	let gridRenderState: TileGridRenderState = currentState.tileGrid;
	const gridContext: TileGridReducerContext = context.createGridContext();

	gridRenderState = tileGridReducers.clearPlaceholder(gridRenderState, gridContext);

	if (isTarget) {
		gridRenderState = tileGridReducers.dropTile(
			gridRenderState,
			gridContext,
			tileBeingDragged,
			placementArea,
			isSource
		);
	} else if (isSource) {
		gridRenderState = tileGridReducers.removeTile(
			gridRenderState,
			gridContext,
			tileBeingDragged.data.id
		);
	}

	return applyTileGrid(currentState, gridRenderState);
}

export function persistGridState(
	currentState: TileGroupRenderState
): TileGroupRenderState {
	const gridRenderState: TileGridRenderState = tileGridReducers.persistTileStates(currentState.tileGrid);

	return applyTileGrid(currentState, gridRenderState);
}

export function deleteTile(
	currentState: TileGroupRenderState,
	context: TileGroupReducerContext,
	tileId: number
): TileGroupRenderState {
	let gridRenderState: TileGridRenderState = currentState.tileGrid;
	const gridContext: TileGridReducerContext = context.createGridContext();

	gridRenderState = tileGridReducers.removeTile(
		gridRenderState,
		gridContext,
		tileId
	);

	return applyTileGrid(currentState, gridRenderState);
}

export function resizeTile(
	currentState: TileGroupRenderState,
	context: TileGroupReducerContext,
	tileId: number,
	dimension: GridDimension,
): TileGroupRenderState {
	let gridRenderState: TileGridRenderState = currentState.tileGrid;
	const gridContext: TileGridReducerContext = context.createGridContext();

	gridRenderState = tileGridReducers.resizeTile(
		gridRenderState,
		gridContext,
		tileId,
		dimension
	);

	return applyTileGrid(currentState, gridRenderState);
}

function applyTileGrid(
	currentState: PartlyPartial<TileGroupRenderState, "tileGrid">,
	tileGridRenderState: TileGridRenderState
): TileGroupRenderState {
	return setPropertiesPreservingRefWhenUnchanged(
		currentState,
		{
			tileGrid: tileGridRenderState,
			dimension: setPropertiesPreservingRefWhenUnchanged(
				currentState.dimension,
				{
					rowSpan: tileGridRenderState.dimension
						.rowSpan + 1 // for the header
				}
			)
		}
	) as TileGroupRenderState;
}

function isVisible(options: TileGroupIsVisibleOptions): boolean {
	if (options.isHiddenFromListing) {
		return false;
	}

	if (options.isHidden) {
		return false;
	}

	return true;
}

function getColumnSpanForGroup(
	width: TileGroupWidth,
	tileGroupGridDimensionLimits: DimensionLimitations
): TileGroupColumnSpanInTileGroupGrid {
	const tileGroupGridColumnSpan: number = groupWidthToTileGroupColumnSpan[width];

	if (tileGroupGridColumnSpan > tileGroupGridDimensionLimits.maxColumns) {
		return {
			value: tileGroupGridDimensionLimits.maxColumns,
			kind: "reduced"
		};
	}

	return {
		value: tileGroupGridColumnSpan,
		kind: "normal"
	};
}

type TileGroupIsVisibleOptions = {
	isHiddenFromListing: boolean;
	isHidden: boolean;
};
